import { LogDomChanges } from "src/api/logDomChanges";
import { Session } from "src/api/session";
import { DomainInfo } from "src/api/domainInfo";

/**
 * List of whitelisted script and iframe sources that should not be tracked
 * These are trusted third-party services and internal scripts
 */
const TRACING_IGNORED_IDS: string[] = [
    "inspector",
    "click-script",
    "trustedFormsScript",
    "LeadIdScript_campaign",
    "click-script-id",
    "metaPixel",
    "maxmind",
    "clarity",
    "googletagmanager",
    "googleapis",
    "LeadiD-ignore-element",
    "LeadiD-ignore-mutation",
    "_next",
    "trustedform",
    "bing",
    "lidstatic",
    "getclicky",
    "twitterPixel",
    "gppongmhjkpfnbhagpmjfkannfbllamg",
    "sc-static",
    "gstatic",
    "snap.licdn",
    "snapchat",
    "tiktok",
    "DummyFrame",
    "redditstatic.com",
    "doubleclick.net",
    "googleadservices.com",
    "connect.facebook.net",
    "sc-static.net/scevent.min.js",
    "https://analytics.tiktok.com/i18n/pixel/events.js",
    "_linkedin_partner_id",
    "snap.licdn.com",
    "js.zi-scripts.com/",
    "ct.pinterest.com/ct.html",
    "analytics.tiktok.com/",
    "@vue/compiler-dom",
    "spinner-line-fade-default",
    "onesignal.com",
    "cdn.onesignal.com",
    "cdn.taboola.com",
    "ct.pinterest.com",
];

/** Pattern to match TrustedForm script IDs (e.g., tf123, tf456) */
const TF_ID_PATTERN = /^tf\d*$/;

/**
 * Configuration for the MutationObserver
 */
const OBSERVER_CONFIG: MutationObserverInit = {
    attributes: true,
    childList: true,
    subtree: true,
};

/**
 * Interface for the initDomTracing parameters
 */
export interface DomTracingParams {
    visitDetails: Session["visitDetails"];
    domain: DomainInfo;
}

/**
 * Checks if a script element should be tracked
 * @param element - The DOM element to check
 * @returns true if the script should be tracked
 */
const shouldTrackScript = (element: Element): boolean => {
    const src = element.getAttribute("src");
    const id = element.getAttribute("id");
    // Check if src or id contains ignored patterns
    const hasIgnoredSrc =
        src && TRACING_IGNORED_IDS.some((item) => src.includes(item));
    const hasIgnoredId =
        id && TRACING_IGNORED_IDS.some((item) => id.includes(item));
    const matchesTfPattern = id && TF_ID_PATTERN.test(id);
    const hasIgnoredText =
        element.outerHTML &&
        TRACING_IGNORED_IDS.some((item) => element.outerHTML.includes(item));
    return (
        !hasIgnoredSrc && !hasIgnoredId && !matchesTfPattern && !hasIgnoredText
    );
};

/**
 * Checks if an iframe element should be tracked
 * @param element - The DOM element to check
 * @returns true if the iframe should be tracked
 */
const shouldTrackIframe = (element: Element): boolean => {
    const className = element.getAttribute("class");
    const src = element.getAttribute("src");
    const name = element.getAttribute("name");

    const hasIgnoredClass =
        className &&
        TRACING_IGNORED_IDS.some((item) => className.includes(item));
    const hasIgnoredSrc =
        src && TRACING_IGNORED_IDS.some((item) => src.includes(item));
    const hasIgnoredName =
        name && TRACING_IGNORED_IDS.some((item) => name.includes(item));

    return !hasIgnoredClass && !hasIgnoredSrc && !hasIgnoredName && !!src;
};

/**
 * Checks if a div or button element should be tracked
 * Only tracks elements that appear to be injected by third-party scripts
 * @param element - The DOM element to check
 * @returns true if the element should be tracked
 */
const shouldTrackElement = (element: Element): boolean => {
    const className = element.getAttribute("class");
    const id = element.getAttribute("id");

    // Ignore elements with our own classes or IDs
    const hasIgnoredClass =
        className &&
        TRACING_IGNORED_IDS.some((item) => className.includes(item));
    const hasIgnoredId =
        id && TRACING_IGNORED_IDS.some((item) => id.includes(item));

    // Track if element has suspicious attributes that indicate third-party injection
    const hasSuspiciousAttrs =
        element.hasAttribute("data-ad") ||
        element.hasAttribute("data-widget") ||
        element.hasAttribute("data-tracking") ||
        element.hasAttribute("data-pixel") ||
        (className?.includes("ad-") ?? false) ||
        (className?.includes("widget-") ?? false) ||
        (className?.includes("popup-") ?? false) ||
        (className?.includes("modal-") ?? false) ||
        (id?.includes("ad-") ?? false) ||
        (id?.includes("widget-") ?? false) ||
        (id?.includes("popup-") ?? false);

    return !hasIgnoredClass && !hasIgnoredId && hasSuspiciousAttrs;
};

/**
 * Initializes DOM change tracking for scripts and iframes
 * Tracks malicious or unauthorized script/iframe injections
 *
 * @param params - Configuration object containing visitDetails and domain
 * @returns A cleanup function to disconnect the observer
 *
 * @example
 * ```tsx
 * const cleanup = initDomTracing({ visitDetails, domain });
 * // Later, to stop tracking:
 * cleanup();
 * ```
 */
export const initDomTracing = (params: DomTracingParams): (() => void) => {
    const { visitDetails, domain } = params;

    const targetNode = document.querySelector<HTMLBodyElement>("body");
    const headTargetNode = document.querySelector<HTMLHeadElement>("head");

    if (!targetNode || !headTargetNode) {
        // eslint-disable-next-line no-console
        console.warn("DOM tracing: Unable to find body or head elements");
        return () => {}; // Return no-op cleanup function
    }

    /**
     * Callback function for MutationObserver
     * Processes DOM mutations and logs suspicious script/iframe additions
     */
    const callback = (mutationList: MutationRecord[]): void => {
        const changes: string[] = [];
        for (const mutation of mutationList) {
            mutation.addedNodes?.forEach((node: Node) => {
                const element = node as Element;

                // Track SCRIPT elements
                if (
                    element.nodeName === "SCRIPT" &&
                    shouldTrackScript(element)
                ) {
                    changes.push(`[SCRIPT] ${element.outerHTML}`);
                }

                // Track IFRAME elements
                if (
                    element.nodeName === "IFRAME" &&
                    shouldTrackIframe(element)
                ) {
                    changes.push(`[IFRAME] ${element.outerHTML}`);
                }

                // Track DIV elements (suspicious third-party injections)
                if (element.nodeName === "DIV" && shouldTrackElement(element)) {
                    changes.push(
                        `[DIV] ${element.outerHTML.substring(0, 500)}...`,
                    );
                }

                // Track BUTTON elements (suspicious third-party injections)
                if (
                    element.nodeName === "BUTTON" &&
                    shouldTrackElement(element)
                ) {
                    changes.push(
                        `[BUTTON] ${element.outerHTML.substring(0, 500)}...`,
                    );
                }
            });
        }

        // Log changes if any detected
        if (changes.length > 0) {
            LogDomChanges({
                visitDetails,
                domain,
                changes,
            });
        }
    };

    const observer = new MutationObserver(callback);

    // Start observing both body and head for changes
    observer.observe(targetNode, OBSERVER_CONFIG);
    observer.observe(headTargetNode, OBSERVER_CONFIG);

    // Return cleanup function to disconnect observer
    return () => {
        observer.disconnect();
    };
};

/**
 * Initializes DOM tracing with browser compatibility check
 * Safe wrapper that checks for MutationObserver support before initializing
 *
 * @param params - Configuration object containing visitDetails and domain
 * @returns A cleanup function to disconnect the observer, or no-op if not supported
 *
 * @example
 * ```tsx
 * useEffect(() => {
 *   const cleanup = initDomTracingSafe({ visitDetails, domain });
 *   return cleanup; // Cleanup on unmount
 * }, []);
 * ```
 */
export const initDomTracingSafe = (params: DomTracingParams): (() => void) => {
    const { visitDetails, domain } = params;

    // Check for browser support and SSR
    if (typeof window === "undefined" || !window.MutationObserver) {
        return () => {}; // Return no-op cleanup function
    }

    // Only track for Google Search or Propel Media traffic on carwarrantyoffers.com
    const isGoogleSearch =
        visitDetails.trafficSourceNetwork?.toLowerCase() === "google search";
    const isPropelMedia =
        visitDetails.trafficSourceNetwork?.toLowerCase() === "propel media";
    const isTargetDomain =
        domain.name?.toLowerCase() === "carwarrantyoffers.com";

    // Track in two scenarios:
    // 1. Domain is carwarrantyoffers.com AND traffic is Google Search
    // 2. OR traffic is Propel Media (any domain)
    const shouldTrack = (isTargetDomain && isGoogleSearch) || isPropelMedia;

    if (!shouldTrack) {
        return () => {}; // Return no-op cleanup function
    }

    return initDomTracing(params);
};
